import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { discoverTenantSupport } from '../../api/backendApi';
import { resetAlgoAccess } from './algoDeviceSupport';
import { supportAccessLabel, writeSupportedTenantSession } from './supportedTenantsSession';

export default function SupportedTenantsPage() {
  const { tenantId: requestedId } = useParams();
  const navigate = useNavigate();
  const { currentUser, authReady } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authReady || dealerAdmin.active) return undefined;
    let live = true;
    setLoading(true);
    discoverTenantSupport()
      .then((response) => {
        if (!live) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        const match = requestedId ? list.find((row) => row.tenantId === requestedId) : null;
        if (match) {
          writeSupportedTenantSession({
            tenantId: match.tenantId,
            name: match.displayName || match.name,
            accessLevel: match.accessLevel,
            tenantAlgoEnabled: match.tenantAlgoEnabled === true,
          });
          resetAlgoAccess();
          navigate('/dashboard', { replace: true });
          return;
        }
        setRows(list);
        if (requestedId) setError('That tenant is not in your support assignments.');
      })
      .catch(() => {
        if (live) setError('Supported tenants could not be loaded.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [authReady, dealerAdmin.active, navigate, requestedId, currentUser?.uid]);

  const openTenant = (row) => {
    writeSupportedTenantSession({
      tenantId: row.tenantId,
      name: row.displayName || row.name,
      accessLevel: row.accessLevel,
      tenantAlgoEnabled: row.tenantAlgoEnabled === true,
    });
    resetAlgoAccess();
    navigate('/dashboard');
  };

  return (
    <MainLayout>
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-900">Supported Tenants</h1>
        <p className="mt-1 text-sm text-gray-600">Open a tenant to use its tenant screens. This does not sign you in as someone else.</p>
        {dealerAdmin.active ? (
          <p className="mt-4 text-sm text-gray-700">Leave the dealer tenant before opening Supported Tenants.</p>
        ) : null}
        {error ? <p className="mt-4 text-sm text-red-700">{error}</p> : null}
        {loading && !dealerAdmin.active ? <p className="mt-4 text-sm text-gray-600">Loading supported tenants…</p> : null}
        {!dealerAdmin.active && !loading ? (
          <ul className="mt-4 divide-y divide-gray-200 rounded border border-gray-200 bg-white">
            {rows.map((row) => (
              <li key={row.tenantId} className="flex items-center justify-between gap-3 px-4 py-3">
                <div>
                  <div className="font-medium text-gray-900">{row.displayName || row.name || row.tenantId}</div>
                  <div className="text-sm text-gray-600">{supportAccessLabel(row.accessLevel)}</div>
                </div>
                <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white" onClick={() => openTenant(row)}>
                  Open tenant
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </MainLayout>
  );
}
